import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import {
  unitCaseEnum,
} from '../../utils/settings';
import { useLocation, useNavigate } from 'react-router-dom';
import ProductCard from './components/ProductCard';
import {
  Box,
  Grid,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
} from '@mui/material';
import CustomButton from '../../v2/MUI_custom_component/Button/CustomButton';
import { trackAnalyticsEvent } from '../../utils/clevertap/clevertapUtils';


const InventoryCart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [sellQty, setSellQty] = useState(
    state?.sellQty || '',
  );
  const showHistory = useSelector(state => state.entityReducer.show_history);
  

  const [selectedCompany, setSelectedCompany] = useState(
    state?.selectedCompany || {},
  );
  const toggleButtonlist = [
    { value: 0, label: state?.selectedCompany?.allocable_quantity?.unit },
    {
      value: 1,
      label: state?.selectedCompany?.allocable_quantity?.base_unit,
    },
  ];
  const [alignment, setAlignment] = useState(0);

  // Track cart page load
  useEffect(() => {
    trackAnalyticsEvent('INVENTORY_CART_PAGE_LOADED', {
      screen_name: 'inventory_cart',
      company_id: selectedCompany?.company_id,
      company_name: selectedCompany?.company_name,
      is_homogeneous: selectedCompany?.is_inventory_homogeneous,
      action: 'page_load'
    });
  }, []);

  const handleAlignment = (event, newAlignment) => {
    if (newAlignment !== null) {
      const previousAlignment = alignment;
      setAlignment(newAlignment);

      // Track unit toggle change in cart
      trackAnalyticsEvent('CART_UNIT_TOGGLE_CHANGED', {
        screen_name: 'inventory_cart',
        company_id: selectedCompany?.company_id,
        previous_unit: previousAlignment === 0 ? 'cases' : 'units',
        new_unit: newAlignment === 0 ? 'cases' : 'units',
        previous_alignment: previousAlignment,
        new_alignment: newAlignment,
        action: 'unit_toggle'
      });
    }
  };
  const {
    cartItemList,
    unitCaseWise,
  } = useSelector((state) => {
    return {
      loginUser: state.loginReducer.loginUser,
      deliveryAddressDetails: state.orderReducer.deliveryAddressDetails,
      company_id: state.orderReducer.company_id,
      distributor: state.orderReducer.distributor,
      cartItemList: state.fmcgReducer.cartItemList,
      unitCaseWise: state.fmcgReducer.unitCaseWise,
      retailers: state.orderReducer.retailers,
      selectedStp: state.bcmOrderReducer.selectedStp,
      seller_id: state.bcmOrderReducer.seller_id,
    };
  });

  const getQuantityInCasesUnit = (
    quantity_in_metric_ton,
    quantity_in_bags,
    multiplier,
    is_inventory_homogeneous,
    unit,
    base_unit,
  ) => {
    if (is_inventory_homogeneous) {
      return quantity_in_metric_ton;
    } else {
      const cases = Math.floor(quantity_in_bags / multiplier);
      const units = quantity_in_bags % multiplier;
      return `${cases} ${unit} ${units} ${base_unit}`;
    }
  };

  const updateProdQty = ({ type, data, inputValue = null }) => {
    // Track cart quantity update
    trackAnalyticsEvent('CART_PRODUCT_QUANTITY_UPDATED', {
      screen_name: 'inventory_cart',
      company_id: selectedCompany?.company_id,
      product_id: data?.product_info?.product_id,
      product_name: data?.product_info?.product_name,
      action_type: type,
      input_value: inputValue,
      current_unit: unitCaseWise === unitCaseEnum['UNIT'] ? 'units' : 'cases'
    });

    dispatch({
      type: 'UPDATE_PROD_QTY',
      data: {
        type: type,
        data: data,
        inputValue: inputValue,
      },
    });
  };
  const calculateTotalQuantity = () => {
    if (selectedCompany.is_inventory_homogeneous) {
      return sellQty || 0;
    } else {
      return cartItemList.reduce((total, prod) => {
        return total + (prod?.sellToBuyerQty?.qty || 0);
      }, 0);
    }
  };
  const getUOM = () => {
    if (selectedCompany.is_inventory_homogeneous) {
      return alignment 
        ? selectedCompany?.allocable_quantity?.base_unit 
        : selectedCompany?.allocable_quantity?.unit;
    } else {
      if (cartItemList.length > 0) {
        return unitCaseWise === unitCaseEnum['UNIT']
          ? cartItemList[0]?.allocable_quantity?.base_unit
          : cartItemList[0]?.allocable_quantity?.unit;
      }
      return 'Units';
    }
  };
  const EmptyCart = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 5,
          textAlign: 'center',
        }}
      >
        <img
          src='https://d2djimvzddgguw.cloudfront.net/humbee-inventory/cart.png'
          alt='Cart Empty'
          style={{ width: '100%', maxWidth: 180, marginBottom: 20 }}
        />
        <Typography variant="h2" sx={{ mb: 2}}>
          Cart Empty!
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          Add products to cart to proceed.
        </Typography>
        <CustomButton
          variant="filled"
          color="primary"
          label="Add Products"
          disabled={showHistory}
          onClick={() => {
            // Track add products navigation from empty cart
            trackAnalyticsEvent('CART_ADD_PRODUCTS_CLICKED', {
              screen_name: 'inventory_cart',
              company_id: selectedCompany?.company_id,
              action: 'add_products_from_empty_cart',
              navigation_target: 'company_inventory'
            });
            navigate('/inventory/company-inventory');
          }}
          sx={{ width: { xs: '80%', sm: 'auto' } }}
        />
      </Box>
    );
  };

  const productListComponent = () => {
    if (!selectedCompany.is_inventory_homogeneous && (!cartItemList || cartItemList.length === 0)) {
      return <EmptyCart />;
    }

    return (
      <Box sx={{ }}>
        {selectedCompany.is_inventory_homogeneous ? (
          <Box>
            <Grid
              container
              spacing={2}
              alignItems='center'
            >
              <Grid
                item
                xs={12}
                sm={8}
                md={9}
                container
                alignItems='center'
                spacing={2}
              >
                <Grid item>
                  <Typography variant='h6'>Available Quantity</Typography>
                </Grid>
                <Grid item>
                  <Typography variant='h6' sx={{ color: '#333333'}}>
                    {alignment
                      ? `${selectedCompany?.allocable_quantity?.quantity_in_bags} ${selectedCompany.allocable_quantity.base_unit}`
                      : `${selectedCompany?.allocable_quantity?.quantity_in_metric_ton} ${selectedCompany.allocable_quantity.unit}`}
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                sm={4}
                md={3}
                container
                justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
              >
                <ToggleButtonGroup
                  value={alignment}
                  exclusive
                  onChange={handleAlignment}
                  aria-label='text alignment'
                >
                  {toggleButtonlist.map((toggleButton) => (
                    <ToggleButton
                      key={toggleButton.value}
                      value={toggleButton.value}
                      aria-label={toggleButton.value}
                    >
                      {toggleButton.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
            </Grid>
            <TextField
              margin='normal'
              fullWidth
              id='sellquantity'
              label='Sell Quantity'
              name='sellquantity'
              value={sellQty}
              onChange={(e) => {
              const newValue = e.target.value;
              setSellQty(newValue);
              
              // Track quantity input change for homogeneous inventory
              trackAnalyticsEvent('CART_HOMOGENEOUS_QUANTITY_CHANGED', {
                screen_name: 'inventory_cart',
                company_id: selectedCompany?.company_id,
                company_name: selectedCompany?.company_name,
                new_quantity: newValue,
                unit: alignment ? selectedCompany?.allocable_quantity?.base_unit : selectedCompany?.allocable_quantity?.unit,
                action: 'quantity_input_change'
              });
            }}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {cartItemList.map((prod, index) => {
              const availableQtyDisplay = unitCaseWise === unitCaseEnum['UNIT']
                ? `${prod?.allocable_quantity?.quantity_in_bags} ${prod?.allocable_quantity?.base_unit}`
                : getQuantityInCasesUnit(
                    prod?.allocable_quantity?.quantity_in_metric_ton,
                    prod?.allocable_quantity?.quantity_in_bags,
                    prod?.allocable_quantity?.multiplier,
                    false,
                    prod?.allocable_quantity?.unit,
                    prod?.allocable_quantity?.base_unit,
                  );

              return (
                <ProductCard 
                  key={index}
                  productName={prod?.product_info?.product_name || ''} 
                  availableQty={availableQtyDisplay}
                  unverifiedQty={null}
                  imageUrl={prod?.product_info?.product_logo || ''} 
                  quantity={prod?.sellToBuyerQty?.qty || 0}
                  onQuantityChange={(newQuantity) => {
                    const currentQty = prod?.sellToBuyerQty?.qty || 0;
                    const type = newQuantity < currentQty ? 'REMOVE' : 'ADD';
                    updateProdQty({ type, data: prod, inputValue: newQuantity })
                  }}
                  showQuantityControls={true}
                  companyName={selectedCompany?.company_name || ''}
                  price={prod?.price_info?.mrp ? prod.price_info.mrp : null}
                  badgeText=''
                  showDeleteButton={true}
                  onDelete={() => {
                    updateProdQty({ type: 'DELETE', data: prod });
                  }}
                  onIncreasePress={() => {
                    updateProdQty({ type: 'ADD', data: prod });
                  }}
                  onReducePress={() => {
                    updateProdQty({
                      type: 'REMOVE',
                      data: prod,
                    });
                  }}
                  onTextChange={(input) => {
                    updateProdQty({
                      type: 'ADD',
                      data: prod,
                      inputValue: Number(input),
                    });
                  }}
                  row={prod}
                />
              );
            })}
          </Box>
        )}
      </Box>
    );
  };
  const isCartEmpty = (
    !selectedCompany.is_inventory_homogeneous && (!cartItemList || cartItemList.length === 0)
  ) || (
    selectedCompany.is_inventory_homogeneous && (!sellQty || Number(sellQty) === 0)
  );

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent:'end', 
        p: { xs: 2, md: 0 },
        mr: { xs: 0, md: 4 }, 
        position: {xs: 'sticky', md: 'relative'}, 
        top: 0, 
        zIndex: 1000, 
        boxShadow: {xs: '0px 2px 8px 0px #0000001A', md: 'none'},
        background: {xs: '#FAFAFA', md: 'transparent'}
       }}>
        <CustomButton
          variant="filled"
          color="primary"
          disabled={isCartEmpty}
          label="Continue to Checkout"
          onClick={() => {
            // Track continue to checkout
            trackAnalyticsEvent('CART_CONTINUE_TO_CHECKOUT_CLICKED', {
              screen_name: 'inventory_cart',
              company_id: selectedCompany?.company_id,
              company_name: selectedCompany?.company_name,
              total_quantity: calculateTotalQuantity(),
              uom: getUOM(),
              is_homogeneous: selectedCompany?.is_inventory_homogeneous,
              cart_items_count: cartItemList?.length || 0,
              sell_qty: selectedCompany?.is_inventory_homogeneous ? sellQty : null,
              action: 'continue_to_checkout',
              navigation_target: 'select_ship_to_party'
            });
            navigate('/inventory/select-ship-to-party');
          }}
          sx={{ width : { xs: '100%', sm: 'auto' } }}
        />
      </Box>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Box>
          <Typography variant="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
            Cart
          </Typography>
          {!isCartEmpty && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="labelD">
                  Total Quantity: 
                </Typography>
                <Typography variant="labelD">
                  {calculateTotalQuantity()} {getUOM()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="labelD">
                  UOM: 
                </Typography>
                <Typography variant="labelD">
                  {getUOM()}
                </Typography>
              </Box>
            </>
          )}
        </Box>
        {selectedCompany.is_inventory_homogeneous && (!sellQty || Number(sellQty) === 0) ? (
          <EmptyCart />
        ) : (
          productListComponent()
        )}
      </Box>
    </>
  );
};

export default InventoryCart;
